import Warning from '@mui/icons-material/Warning';
import {
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    TextField,
    Tooltip
} from '@mui/material';
import Typography from '@mui/material/Typography';
import { lightBlue } from '@mui/material/colors';
import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import root from 'react-shadow';
import ErrorUtil from '@/util/ErrorUtil';
import ReactComponent from '../ReactComponent';
import VisualizadorVersoesMinutaVoto from './VisualizadorVersoesMinutaVoto';

const LIMITE_CARACTERES = 400;

class ConteudoVoto extends ReactComponent {
    observacaoDevolucao;

    caracteres = 0;
    aberto = false;

    constructor(props) {
        super(props);

        makeObservable(this, {
            caracteres: observable,
            aberto: observable,
            registro: computed,
            abrir: action,
            fechar: action,
            contarCaracteres: action,
            acordaoAssinado: computed,
            acordaoAssinadoPJe: computed
        });
    }

    get registro() {
        return this.observableProps.registro;
    }

    abrir = () => {
        this.aberto = true;
    };

    fechar = () => {
        this.limparObservacaoDevolucao();
        this.aberto = false;
    };

    contarCaracteres(qtd) {
        this.caracteres = qtd;
    }

    async componentDidMount() {
        try {
            if (this.registro) {
                if (!this.registro.editorMinutaVoto) {
                    this.salaSessaoStore.carregarEditorVoto(this.registro);
                }

                if (!this.registro.conteudoVoto) {
                    await this.salaSessaoStore.carregarConteudoVoto(this.registro);
                }
            }
        } catch (error) {
            console.error(`Falha na inicialização do componente ConteudoVoto: ${ErrorUtil.getMensagemErro(error)}`);
            throw new Error('Falha na inicialização do componente ConteudoVoto', { cause: error });
        }
    }

    reactionRegistro = reaction(
        () => this.registro,
        async () => {
            await this.salaSessaoStore.carregarEditorVoto(this.registro);
            await this.salaSessaoStore.carregarConteudoVoto(this.registro);
        }
    );

    handleClickAcordaoValido = async () => {
        let novoValor = !this.registro.acordaoValidado;
        await this.acordaoService.validarAcordao(
            this.registro.sessaoId,
            this.registro.processo.numeroCompleto,
            novoValor
        );
        runInAction(() => (this.registro.acordaoValidado = novoValor));
    };

    handleClickAcordaoObrigatorio = async () => {
        let novoValor = !this.registro.acordaoObrigatorio;
        await this.acordaoService.marcarAcordaoObrigatorio(
            this.registro.sessaoId,
            this.registro.processo.numeroCompleto,
            novoValor
        );
        runInAction(() => (this.registro.acordaoObrigatorio = novoValor));
    };

    limparObservacaoDevolucao = () => {
        this.observacaoDevolucao = null;
    };

    devolverAcordao = async () => {
        try {
            let novoValor = !this.registro.acordaoAssinado;
            await this.acordaoService.devolverAcordao(
                this.registro.sessaoId,
                this.registro.processo.numeroCompleto,
                this.observacaoDevolucao
            );
            runInAction(() => (this.registro.acordaoAssinado = novoValor));
            this.mensagemStore.abrirMensagemSucesso('Acordão devolvido com sucesso');
        } catch (error) {
            this.mensagemStore.abrirMensagemErro(error.message);
            this.limparObservacaoDevolucao();
        } finally {
            runInAction(() => (this.aberto = false));
            this.limparObservacaoDevolucao();
        }
    };

    changeObservacaoDevolucao = (event) => {
        this.observacaoDevolucao = event.target.value;
        this.contarCaracteres(this.observacaoDevolucao.length);
    };

    get acordaoAssinado() {
        return this.registro.acordaoAssinado || this.registro.dataAssinaturaSistemaOrigem;
    }

    get acordaoAssinadoPJe() {
        return !this.registro.acordaoAssinado && this.registro.dataAssinaturaSistemaOrigem;
    }

    render() {
        return (
            <div style={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div id="controleAcordao">
                        {this.registro.acordaoObrigatorio && (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                {this.acordaoAssinado && !this.acordaoAssinadoPJe ? (
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        {this.renderAcordaoVerificado()}
                                        {this.renderDevolverAcordaoInvalido()}
                                        {this.renderDialogObservacaoDevolucao()}
                                    </div>
                                ) : (
                                    this.renderAlerta()
                                )}
                            </div>
                        )}
                    </div>
                    <div
                        id="controleMinuta"
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
                    >
                        {this.renderEditorMinuta()}
                        {this.renderVisualizadorVersoesMinuta()}
                    </div>
                </div>
                {this.renderConteudoVoto()}
            </div>
        );
    }

    renderVisualizadorVersoesMinuta() {
        return (
            this.registro.editorMinutaVoto &&
            this.registro.conteudoVoto && <VisualizadorVersoesMinutaVoto registro={this.registro} />
        );
    }

    renderEditorMinuta() {
        return (
            this.registro.editorMinutaVoto && (
                <>
                    <Typography
                        display="inline"
                        sx={{ color: lightBlue[900], fontWeight: 'bold' }}
                    >
                        Editor da minuta:&nbsp;
                    </Typography>
                    <Typography
                        display="inline"
                        sx={{ fontWeight: 'bold' }}
                    >
                        {this.registro.editorMinutaVoto}
                    </Typography>
                </>
            )
        );
    }

    renderAcordaoVerificado() {
        return (
            <Tooltip
                title={
                    this.registro.acordaoTransferido
                        ? 'Não é permitido alterar após o envio do acórdão ao PJe'
                        : 'Mantenha marcado para deixar o acórdão como verificado'
                }
                placement="top-start"
            >
                <FormControlLabel
                    style={{ paddingLeft: '10px', height: '40px' }}
                    disabled={!this.acordaoAssinado || this.registro.acordaoTransferido}
                    label={<Typography style={{ fontWeight: 'bold' }}>Acórdão verificado</Typography>}
                    control={
                        <Checkbox
                            color="primary"
                            onClick={this.handleClickAcordaoValido}
                            checked={this.registro.acordaoValidado}
                        />
                    }
                />
            </Tooltip>
        );
    }

    renderDevolverAcordaoInvalido() {
        return (
            !this.registro.acordaoValidado &&
            !this.registro.acordaoTransferido &&
            (this.isComponenteHabilitado('EnvioAcordaoSIJ') || this.salaSessaoStore.isProcessoPje(this.registro) ? (
                <Button
                    variant="outlined"
                    onClick={this.abrir}
                    color="primary"
                    style={{ height: '32px' }}
                >
                    Devolver acórdão inválido
                </Button>
            ) : (
                <Tooltip title="Acórdãos de processos do SIJ devem ser devolvidos para correção por meio do Sistema de Publicações Judiciais (SPJ).">
                    <Warning style={{ fill: 'darkorange', marginRight: '5px' }} />
                </Tooltip>
            ))
        );
    }

    renderDialogObservacaoDevolucao() {
        return (
            <Dialog
                open={this.aberto}
                onClose={this.fechar}
                fullWidth
                maxWidth={'md'}
                slotProps={{
                    paper: {
                        sx: { width: '400px' }
                    }
                }}
            >
                <DialogTitle>Observação de devolução</DialogTitle>
                <DialogContent>
                    <TextField
                        multiline={true}
                        rows={4}
                        maxRows={10}
                        value={this.observacaoDevolucao}
                        onInput={this.changeObservacaoDevolucao.bind(this)}
                        onChange={this.changeObservacaoDevolucao.bind(this)}
                        error={this.caracteres >= LIMITE_CARACTERES}
                        fullWidth
                        slotProps={{
                            htmlInput: { maxLength: LIMITE_CARACTERES }
                        }}
                    />
                </DialogContent>
                <DialogActions style={{ justifyContent: 'space-between' }}>
                    <span
                        style={{
                            color: this.caracteres >= LIMITE_CARACTERES ? 'red' : 'black',
                            fontSize: 'x-small',
                            paddingLeft: '16px'
                        }}
                    >
                        Caracteres: {this.caracteres} de {LIMITE_CARACTERES}
                    </span>
                    <span style={{ display: 'flex', justifyContent: '' }}>
                        <span>
                            <Button
                                onClick={this.fechar}
                                color="secondary"
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={this.devolverAcordao}
                                color="primary"
                            >
                                Devolver
                            </Button>
                        </span>
                    </span>
                </DialogActions>
            </Dialog>
        );
    }

    renderAlerta() {
        return (
            <Tooltip
                placement="top"
                title={this.tooltipSituacaoAcordao()}
            >
                <Warning style={{ fill: 'darkorange' }} />
            </Tooltip>
        );
    }

    tooltipSituacaoAcordao() {
        const tooltipAcordaoObrigatorio = this.registro.acordaoObrigatorio
            ? 'O acórdão ainda não foi assinado.'
            : 'O acórdão não deverá ser assinado, pois não será publicado nesta fase processual.';
        return this.acordaoAssinadoPJe ? 'O acórdão foi assinado no sistema de origem.' : tooltipAcordaoObrigatorio;
    }

    renderConteudoVoto() {
        return (
            <div
                style={{
                    display: 'flex',
                    flexGrow: 1,
                    overflow: 'auto',
                    color: lightBlue[900],
                    boxSizing: 'border-box',
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                    borderStyle: 'solid',
                    borderWidth: '1px',
                    borderRadius: '4px'
                }}
            >
                {!this.registro.conteudoVoto && this.appStore.requisicoesPendentes ? (
                    <CircularProgress
                        style={{ margin: '10px' }}
                        disableShrink={true}
                    />
                ) : (
                    <root.div>
                        <div
                            data-sentry-mask
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                width: this.appStore.larguraJanela * 0.48 + 'px',
                                padding: '20px',
                                marginLeft: '20px',
                                minHeight: '90px',
                                maxHeight: '40px',
                                color: 'black'
                            }}
                            dangerouslySetInnerHTML={{ __html: this.registro.conteudoVoto }}
                        ></div>
                    </root.div>
                )}
            </div>
        );
    }
}

export default inject('stores')(observer(ConteudoVoto));
